/**
 * Configuration des URLs des services API
 * Les URLs sont chargées depuis les variables d'environnement avec support DEV/PROD
 */

// Déterminer l'environnement depuis VITE_ENV ou fallback automatique
const getEnvironment = (): 'dev' | 'prod' => {
  const env = import.meta.env.VITE_ENV?.toLowerCase()
  if (env === 'dev' || env === 'development') return 'dev'
  if (env === 'prod' || env === 'production') return 'prod'
  
  // Fallback automatique basé sur le mode Vite ou hostname
  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    return 'dev'
  }
  return 'prod'
}

const currentEnv = getEnvironment()

// 🔍 DEBUG: Vérification du chargement des variables d'environnement par Vite
// ⚠️ Affiché aussi en PRODUCTION pour faciliter le debug
console.group(`🔍 [${currentEnv.toUpperCase()}] Variables d'environnement chargées par Vite`)
console.log('VITE_ENV:', import.meta.env.VITE_ENV)
console.log('Environnement détecté:', currentEnv)
console.log('')
console.log('📦 Variables DEV:')
console.log('  VITE_BACKEND_CORE_URL_DEV:', import.meta.env.VITE_BACKEND_CORE_URL_DEV)
console.log('  VITE_BUCKET_SERVER_DNS_DEV:', import.meta.env.VITE_BUCKET_SERVER_DNS_DEV)
console.log('  VITE_BUCKET_SECRET_KEY_DEV:', import.meta.env.VITE_BUCKET_SECRET_KEY_DEV ? '✅ Chargée' : '❌ Non chargée')
console.log('')
console.log('📦 Variables PROD:')
console.log('  VITE_BACKEND_CORE_URL_PROD:', import.meta.env.VITE_BACKEND_CORE_URL_PROD)
console.log('  VITE_BUCKET_SERVER_DNS_PROD:', import.meta.env.VITE_BUCKET_SERVER_DNS_PROD)
console.log('  VITE_BUCKET_SECRET_KEY_PROD:', import.meta.env.VITE_BUCKET_SECRET_KEY_PROD ? '✅ Chargée' : '❌ Non chargée')
console.groupEnd()

// Helper pour récupérer une URL selon l'environnement
const getUrl = (devUrl: string | undefined, prodUrl: string | undefined, fallback: string): string => {
  if (currentEnv === 'dev') {
    return devUrl || fallback
  }
  return prodUrl || fallback
}

// URLs de développement (fallback si variables non définies)
const DEV_URLS_FALLBACK = {
  BACKEND_CORE: 'http://localhost:8000/api',
  BUCKET_SERVER_DNS: 'https://bucket-signalement.courdescomptes.bj',
  BUCKET_SECRET_KEY: 'signalement@2025',
}

// URLs de production (fallback si variables non définies)
const PROD_URLS_FALLBACK = {
  BACKEND_CORE: 'https://backend-signalement.courdescomptes.bj/api',
  BUCKET_SERVER_DNS: 'https://bucket-signalement.courdescomptes.bj',
  BUCKET_SECRET_KEY: 'signalement@2025',
}

// Helper pour ajouter /api si nécessaire (seulement pour les variables d'environnement)
const addApiIfNeeded = (url: string | undefined, fallback: string): string => {
  if (!url) return fallback // Utiliser le fallback tel quel (déjà avec /api)
  // Si l'URL ne se termine pas par /api, l'ajouter
  return url.endsWith('/api') ? url : url + '/api'
}

// Configuration des URLs selon l'environnement
export const API_CONFIG = {
  BACKEND_CORE: currentEnv === 'dev'
    ? addApiIfNeeded(import.meta.env.VITE_BACKEND_CORE_URL_DEV, DEV_URLS_FALLBACK.BACKEND_CORE)
    : addApiIfNeeded(import.meta.env.VITE_BACKEND_CORE_URL_PROD, PROD_URLS_FALLBACK.BACKEND_CORE),
  BUCKET_SERVER_DNS: getUrl(
    import.meta.env.VITE_BUCKET_SERVER_DNS_DEV,
    import.meta.env.VITE_BUCKET_SERVER_DNS_PROD,
    currentEnv === 'dev' ? DEV_URLS_FALLBACK.BUCKET_SERVER_DNS : PROD_URLS_FALLBACK.BUCKET_SERVER_DNS
  ),
  BUCKET_SECRET_KEY: getUrl(
    import.meta.env.VITE_BUCKET_SECRET_KEY_DEV,
    import.meta.env.VITE_BUCKET_SECRET_KEY_PROD,
    currentEnv === 'dev' ? DEV_URLS_FALLBACK.BUCKET_SECRET_KEY : PROD_URLS_FALLBACK.BUCKET_SECRET_KEY
  ),
}

// 🔍 DEBUG: Afficher les URLs finales utilisées
// ⚠️ Affiché aussi en PRODUCTION pour faciliter le debug
console.group(`🌐 [${currentEnv.toUpperCase()}] URLs finales configurées (API_CONFIG)`)
console.log('Environnement actif:', currentEnv)
console.log('BACKEND_CORE:', API_CONFIG.BACKEND_CORE)
console.log('BUCKET_SERVER_DNS:', API_CONFIG.BUCKET_SERVER_DNS)
console.log('BUCKET_SECRET_KEY:', API_CONFIG.BUCKET_SECRET_KEY ? '✅ Configurée' : '❌ Non configurée')
console.groupEnd()

// Exporter l'environnement actuel pour debug
export const CURRENT_ENV = currentEnv

// Exporter les URLs individuelles pour faciliter l'utilisation
export const BACKEND_CORE_URL = API_CONFIG.BACKEND_CORE
export const BUCKET_SERVER_DNS = API_CONFIG.BUCKET_SERVER_DNS
export const BUCKET_SECRET_KEY = API_CONFIG.BUCKET_SECRET_KEY

